<script setup>
import {
  ref,
  computed,
  watch,
  shallowRef,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  status: { type: Object, default: null },
});
const emit = defineEmits(['update:modelValue']);

const store = useStore();
const { t } = useI18n();

const reminders = computed(
  () => store.getters['planningEventReminders/getRecords'] || []
);
const visibleReminders = computed(() =>
  !showForm.value ? reminders.value : []
);
const uiFlags = computed(
  () => store.getters['planningEventReminders/getUIFlags']
);

const TEMPLATE_VARIABLES = [
  { name: '{{event.name}}', label: 'Event Name' },
  { name: '{{start_date}}', label: 'Start Date' },
  { name: '{{end_date}}', label: 'End Date' },
  { name: '{{customer.name}}', label: 'Customer Name' },
  { name: '{{event_manager.name}}', label: 'Event Manager' },
];

const OFFSET_UNITS = ['hours', 'days', 'weeks'];
const OFFSET_UNITS_ADMIN = ['minutes', 'hours', 'days', 'weeks'];

const isAdmin = computed(
  () => store.getters['auth/getCurrentUser']?.role === 'administrator'
);

const availableOffsetUnits = computed(() =>
  isAdmin.value ? OFFSET_UNITS_ADMIN : OFFSET_UNITS
);
const RELATIVE_TO_OPTIONS = [
  'event_start_date',
  'event_end_date',
  'creation_date',
  'time_in_status',
];
const RELATIVE_TO_LABEL_KEYS = {
  event_start_date: 'RELATIVE_TO_EVENT_START_DATE',
  event_end_date: 'RELATIVE_TO_EVENT_END_DATE',
  creation_date: 'RELATIVE_TO_CREATION_DATE',
  time_in_status: 'RELATIVE_TO_TIME_IN_STATUS',
};

const emptyForm = () => ({
  recipient_type: 'event_manager',
  message_template: '',
  offset_value: 1,
  offset_unit: 'days',
  offset_direction: 'before',
  relative_to: 'event_start_date',
  is_recurring: false,
  frequency_value: 1,
  frequency_unit: 'days',
  ai_vary: false,
  skip_if_active_conversation: false,
});

const showForm = ref(false);
const editingReminderId = ref(null);
const deletingReminderId = ref(null);
const messageEditor = shallowRef(null);

const createMessageEditor = content => {
  messageEditor.value?.destroy();
  const htmlContent = content
    ? `<p>${content.replace(/\n/g, '</p><p>')}</p>`
    : '<p></p>';
  messageEditor.value = new Editor({
    content: htmlContent,
    extensions: [StarterKit],
    onUpdate: ({ editor }) => {
      form.value.message_template = editor.getText({ blockSeparator: '\n' });
    },
    editorProps: {
      attributes: {
        class: 'min-h-[80px] px-3 py-2.5 text-sm outline-none',
      },
    },
  });
};

const insertTemplateVariable = variableName => {
  messageEditor.value?.chain().focus().insertContent(variableName).run();
};
const form = ref(emptyForm());

const close = () => {
  emit('update:modelValue', false);
};

const openCreateForm = () => {
  editingReminderId.value = null;
  form.value = emptyForm();
  showForm.value = true;
};

const openEditForm = reminder => {
  editingReminderId.value = reminder.id;
  form.value = {
    recipient_type: reminder.recipient_type,
    message_template: reminder.message_template,
    offset_value: reminder.offset_value,
    offset_unit: reminder.offset_unit,
    offset_direction: reminder.offset_direction ?? 'before',
    relative_to: reminder.relative_to,
    is_recurring: reminder.is_recurring,
    frequency_value: reminder.frequency_value ?? 1,
    frequency_unit: reminder.frequency_unit ?? 'days',
    ai_vary: reminder.ai_vary ?? false,
    skip_if_active_conversation: reminder.skip_if_active_conversation ?? false,
  };
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  editingReminderId.value = null;
  form.value = emptyForm();
};

const saveReminder = async () => {
  if (!form.value.message_template.trim()) return;

  const payload = {
    status_type: props.status.type,
    status_key: props.status.key,
    recipient_type: form.value.recipient_type,
    message_template: form.value.message_template,
    offset_value: form.value.offset_value,
    offset_unit: form.value.offset_unit,
    offset_direction: form.value.offset_direction,
    relative_to: form.value.relative_to,
    is_recurring: form.value.is_recurring,
    frequency_value: form.value.is_recurring
      ? form.value.frequency_value
      : null,
    frequency_unit: form.value.is_recurring ? form.value.frequency_unit : null,
    ai_vary: form.value.ai_vary,
    skip_if_active_conversation:
      form.value.recipient_type === 'customer'
        ? form.value.skip_if_active_conversation
        : false,
    active: true,
  };

  try {
    if (editingReminderId.value) {
      await store.dispatch('planningEventReminders/update', {
        id: editingReminderId.value,
        ...payload,
      });
    } else {
      await store.dispatch('planningEventReminders/create', payload);
    }
    cancelForm();
  } catch {
    // errors handled by store
  }
};

const deleteReminder = async id => {
  deletingReminderId.value = id;
  try {
    await store.dispatch('planningEventReminders/delete', id);
  } finally {
    deletingReminderId.value = null;
  }
};

watch(showForm, async val => {
  if (val) {
    await nextTick();
    createMessageEditor(form.value.message_template);
  } else {
    messageEditor.value?.destroy();
    messageEditor.value = null;
  }
});

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  messageEditor.value?.destroy();
});

function onKeydown(e) {
  if (e.key === 'Escape' && props.modelValue) close();
}

// Load reminders when modal opens / status changes
watch(
  () => [props.modelValue, props.status],
  async ([open, status]) => {
    if (!open || !status) return;
    cancelForm();
    await store.dispatch('planningEventReminders/get', {
      status_type: status.type,
      status_key: status.key,
    });
  },
  { immediate: true }
);

const recipientLabel = type =>
  type === 'customer'
    ? t('PLANNING.REMINDERS.RECIPIENT_CUSTOMER')
    : t('PLANNING.REMINDERS.RECIPIENT_EVENT_MANAGER');

const reminderSummary = reminder => {
  const unit = t(
    `PLANNING.REMINDERS.UNIT_${reminder.offset_unit.toUpperCase()}`
  );
  const direction = reminder.offset_direction ?? 'before';
  const relativeTo = t(
    `PLANNING.REMINDERS.${RELATIVE_TO_LABEL_KEYS[reminder.relative_to]}`
  );
  return `${reminder.offset_value} ${unit} ${direction} ${relativeTo}`;
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-n-surface-1/25 backdrop-blur-sm"
      @click.self="close"
    >
      <div
        class="w-full max-w-lg rounded-xl bg-white dark:bg-slate-950 shadow-2xl mx-4 flex flex-col max-h-[90vh]"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h3 class="text-base font-semibold text-slate-900 dark:text-white">
            {{ t('PLANNING.REMINDERS.MODAL_TITLE', { status: status?.name }) }}
          </h3>
          <Button icon="i-lucide-x" slate ghost @click="close" />
        </div>

        <!-- Reminder list -->
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <div
            v-if="uiFlags.isFetching"
            class="text-sm text-slate-500 text-center py-4"
          >
            Loading...
          </div>
          <div
            v-else-if="!reminders.length && !showForm"
            class="text-sm text-slate-500 text-center py-4"
          >
            {{ t('PLANNING.REMINDERS.NO_REMINDERS') }}
          </div>

          <div
            v-for="reminder in visibleReminders"
            :key="reminder.id"
            class="reminder-preview flex items-start justify-between gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <span
                  class="text-sm font-medium bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 px-2 py-0.5 rounded-full"
                >
                  {{ recipientLabel(reminder.recipient_type) }}
                </span>
                <span
                  v-if="reminder.is_recurring"
                  class="text-sm text-slate-500"
                >
                  · recurring
                </span>
              </div>
              <p class="text-sm text-n-slate-11 truncate mb-0">
                {{ reminder.message_template }}
              </p>
              <p class="text-sm text-slate-400 flex items-center gap-1">
                <Icon icon="i-lucide-bell" class="w-4 h-4" />
                {{ reminderSummary(reminder) }}
              </p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <Button
                v-tooltip.top="'Edit'"
                icon="i-lucide-pen-line"
                slate
                faded
                @click="openEditForm(reminder)"
              />
              <Button
                v-tooltip.top="'Delete'"
                icon="i-lucide-trash-2"
                ruby
                faded
                :loading="deletingReminderId === reminder.id"
                @click="deleteReminder(reminder.id)"
              />
            </div>
          </div>

          <!-- Inline form -->
          <div v-if="showForm" class="space-y-4 mt-2">
            <!-- Recipient -->
            <div>
              <label class="block text-sm font-semibold text-n-slate-12 mb-1">
                {{ t('PLANNING.REMINDERS.RECIPIENT_LABEL') }}
              </label>
              <select
                v-model="form.recipient_type"
                class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800 text-sm"
              >
                <option value="event_manager">
                  {{ t('PLANNING.REMINDERS.RECIPIENT_EVENT_MANAGER') }}
                </option>
                <option value="customer">
                  {{ t('PLANNING.REMINDERS.RECIPIENT_CUSTOMER') }}
                </option>
              </select>
              <label
                v-if="form.recipient_type === 'customer'"
                class="mt-2 flex items-center gap-2 cursor-pointer"
              >
                <Switch v-model="form.skip_if_active_conversation" />
                <span class="text-sm text-n-slate-11">
                  {{ t('PLANNING.REMINDERS.SKIP_IF_ACTIVE_CONVERSATION') }}
                </span>
                <span
                  v-tooltip="{
                    content: t(
                      'PLANNING.REMINDERS.SKIP_IF_ACTIVE_CONVERSATION_HINT'
                    ),
                    placement: 'top',
                  }"
                  class="i-lucide-info size-3.5 text-n-slate-9 cursor-default"
                />
              </label>
            </div>

            <!-- Message template -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="text-sm font-semibold text-n-slate-12">
                  {{ t('PLANNING.REMINDERS.MESSAGE_LABEL') }}
                </label>
                <label
                  v-if="form.is_recurring"
                  class="flex items-center gap-1.5 cursor-pointer"
                >
                  <span class="text-sm text-n-slate-11">Vary with AI</span>
                  <span
                    v-tooltip="{
                      content:
                        'Each recurring send will be rewritten by AI using event details and recent conversation context, so the message never sounds repetitive.',
                      placement: 'top',
                    }"
                    class="i-lucide-info size-3.5 text-n-slate-9 cursor-default"
                  />
                  <Switch v-model="form.ai_vary" />
                </label>
              </div>
              <!-- Tiptap editor -->
              <div
                class="p-3 py-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus-within:border-woot-500 dark:focus-within:border-woot-500 cursor-text transition-colors"
                @click="messageEditor?.commands.focus()"
              >
                <EditorContent :editor="messageEditor" />
              </div>
              <!-- Variable chips -->
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="variable in TEMPLATE_VARIABLES"
                  :key="variable.name"
                  type="button"
                  class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 rounded-full border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  @click="insertTemplateVariable(variable.name)"
                >
                  <span class="i-lucide-plus size-3" />
                  {{ variable.label }}
                </button>
              </div>
            </div>

            <!-- Offset -->
            <div>
              <label class="block text-sm font-semibold text-n-slate-12 mb-1">
                {{ t('PLANNING.REMINDERS.OFFSET_LABEL') }}
              </label>
              <div class="flex items-center gap-2">
                <input
                  v-model.number="form.offset_value"
                  type="number"
                  min="1"
                  class="!w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800 text-sm"
                />
                <select
                  v-model="form.offset_unit"
                  class="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                >
                  <option
                    v-for="unit in availableOffsetUnits"
                    :key="unit"
                    :value="unit"
                  >
                    {{ t(`PLANNING.REMINDERS.UNIT_${unit.toUpperCase()}`) }}
                  </option>
                </select>
                <select
                  v-model="form.offset_direction"
                  class="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="before">Before</option>
                  <option value="after">After</option>
                </select>
              </div>
            </div>

            <!-- Relative to -->
            <div>
              <label class="block text-sm font-semibold text-n-slate-12 mb-1">
                {{ t('PLANNING.REMINDERS.RELATIVE_TO_LABEL') }}
              </label>
              <select
                v-model="form.relative_to"
                class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800 text-sm"
              >
                <option
                  v-for="opt in RELATIVE_TO_OPTIONS"
                  :key="opt"
                  :value="opt"
                >
                  {{ t(`PLANNING.REMINDERS.${RELATIVE_TO_LABEL_KEYS[opt]}`) }}
                </option>
              </select>
            </div>

            <!-- Recurring toggle -->
            <div class="flex items-center gap-3 pt-4 pb-2">
              <Switch
                v-model="form.is_recurring"
                @change="form.is_recurring || (form.ai_vary = false)"
              />
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t('PLANNING.REMINDERS.RECURRING_LABEL') }}
              </span>
            </div>

            <!-- Frequency (visible when recurring) -->
            <div v-if="form.is_recurring">
              <label class="block text-sm font-semibold text-n-slate-12 mb-1">
                {{ t('PLANNING.REMINDERS.FREQUENCY_LABEL') }}
              </label>
              <div class="flex items-center gap-2">
                <input
                  v-model.number="form.frequency_value"
                  type="number"
                  min="1"
                  class="!w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800 text-sm"
                />
                <select
                  v-model="form.frequency_unit"
                  class="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                >
                  <option
                    v-for="unit in availableOffsetUnits"
                    :key="unit"
                    :value="unit"
                  >
                    {{ t(`PLANNING.REMINDERS.UNIT_${unit.toUpperCase()}`) }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-n-weak">
          <Button
            v-if="!showForm"
            :label="t('PLANNING.REMINDERS.ADD_REMINDER')"
            icon="i-lucide-plus"
            class="w-full"
            outlined
            @click="openCreateForm"
          />
          <div v-if="showForm" class="flex items-center gap-2 pt-1">
            <Button
              :label="t('PLANNING.REMINDERS.CANCEL')"
              slate
              outlined
              class="w-full"
              @click="cancelForm"
            />
            <Button
              :label="t('PLANNING.REMINDERS.SAVE')"
              :loading="uiFlags.isCreating || uiFlags.isUpdating"
              class="w-full"
              @click="saveReminder"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
